/**
 * DemoStateProvider — the demo's entire mutable state, held in memory.
 *
 * This is what makes the demo feel real (add a client → it appears; finish a
 * session → it shows up under that client's history) while guaranteeing "resets
 * every visit": there is no storage, no backend, no persistence. A reload re-seeds
 * from mock-data and forgets everything. Nothing here is PHI.
 */
import { createContext, useContext, useEffect, useMemo, useReducer, type ReactNode } from "react";
import {
  DEMO_CLIENTS,
  DEMO_SESSIONS,
  sessionsForClient,
  lastSessionDate,
  type DemoClient,
  type DemoSession,
  type SessionSettings,
} from "./mock-data";
import { SEED_SCRIPTS, type DemoScript } from "./demo-scripts";
import { getSocket } from "../shims/socket-client";
import { LIVE } from "./live/live";
import { canBroadcast } from "./live/useSyncedState";

export interface DemoState {
  clients: DemoClient[];
  sessions: DemoSession[];
  scripts: DemoScript[];
}

export type DemoAction =
  | { type: "ADD_CLIENT"; client: DemoClient }
  | { type: "ADD_SESSION"; session: DemoSession }
  | { type: "ADD_SCRIPT"; script: DemoScript }
  | { type: "REMOVE_SCRIPT"; id: string }
  | { type: "UPDATE_PREFS"; clientId: string; prefs: SessionSettings }
  | { type: "RESET" }
  // Live co-drive: full-state snapshot the host pushes to a late joiner (sync_request).
  | { type: "__REPLACE__"; state: DemoState };

function reducer(state: DemoState, action: DemoAction): DemoState {
  switch (action.type) {
    case "__REPLACE__":
      return action.state;
    case "ADD_CLIENT":
      // Newest first so the just-added pseudo-client is obvious on the dashboard.
      return { ...state, clients: [action.client, ...state.clients] };
    case "ADD_SESSION":
      // Only ever called with a real clientId — the demo's guardrail blocks
      // saving a session that has no client attached.
      return { ...state, sessions: [action.session, ...state.sessions] };
    case "ADD_SCRIPT":
      // Newest first so a just-created script lands at the top of its category.
      return { ...state, scripts: [action.script, ...state.scripts] };
    case "REMOVE_SCRIPT":
      return { ...state, scripts: state.scripts.filter((s) => s.id !== action.id) };
    case "UPDATE_PREFS":
      return {
        ...state,
        clients: state.clients.map((c) => (c.id === action.clientId ? { ...c, savedPrefs: action.prefs } : c)),
      };
    case "RESET":
      return seed();
    default:
      return state;
  }
}

function seed(): DemoState {
  // Fresh copies so in-session edits never mutate the module-level seed arrays.
  return {
    clients: DEMO_CLIENTS.map((c) => ({ ...c })),
    sessions: DEMO_SESSIONS.map((s) => ({ ...s })),
    scripts: SEED_SCRIPTS.map((s) => ({ ...s })),
  };
}

interface DemoContextValue extends DemoState {
  addClient: (client: DemoClient) => void;
  addSession: (session: DemoSession) => void;
  addScript: (script: DemoScript) => void;
  removeScript: (id: string) => void;
  updatePrefs: (clientId: string, prefs: SessionSettings) => void;
  reset: () => void;
  /** Derived selectors. */
  clientById: (id: string) => DemoClient | undefined;
  sessionsFor: (clientId: string) => DemoSession[];
  lastSessionFor: (clientId: string) => string | null;
}

const DemoContext = createContext<DemoContextValue | null>(null);

export function DemoStateProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(reducer, undefined, seed);

  // In LIVE mode, apply reducer actions the other screen relays. Remote actions go
  // through raw `dispatch` (NOT `relay`) so they are never re-broadcast — that is the
  // echo guard. `__REPLACE__` (the host's join snapshot) flows through the same
  // channel. Inert in the static build.
  useEffect(() => {
    if (!LIVE) return;
    const socket = getSocket();
    const onRemote = (action: DemoAction) => dispatch(action);
    socket.on("demo_action", onRemote);
    return () => {
      socket.off("demo_action", onRemote);
    };
  }, []);

  const value = useMemo<DemoContextValue>(() => {
    // Local mutations: apply here AND relay to the other screen. Either person can
    // trigger these in a co-driven demo; remote actions arrive through raw `dispatch`
    // above, so nothing is ever relayed twice.
    const relay = (action: DemoAction) => {
      dispatch(action);
      // Relay ONLY inside a live room — and, for a guest, only once the host's join
      // snapshot has landed (a pre-sync action would fork the shared data). In a LIVE
      // build with no room (a solo visitor — loopback mode), emitting would loop back to
      // the listener above and double-apply the action (duplicate clients/sessions).
      if (canBroadcast()) getSocket().emit("demo_action", action);
    };
    return {
      ...state,
      addClient: (client) => relay({ type: "ADD_CLIENT", client }),
      addSession: (session) => relay({ type: "ADD_SESSION", session }),
      addScript: (script) => relay({ type: "ADD_SCRIPT", script }),
      removeScript: (id) => relay({ type: "REMOVE_SCRIPT", id }),
      updatePrefs: (clientId, prefs) => relay({ type: "UPDATE_PREFS", clientId, prefs }),
      reset: () => relay({ type: "RESET" }),
      clientById: (id) => state.clients.find((c) => c.id === id),
      sessionsFor: (clientId) => sessionsForClient(state.sessions, clientId),
      lastSessionFor: (clientId) => lastSessionDate(state.sessions, clientId),
    };
  }, [state]);

  return <DemoContext.Provider value={value}>{children}</DemoContext.Provider>;
}

export function useDemoState(): DemoContextValue {
  const ctx = useContext(DemoContext);
  if (!ctx) throw new Error("useDemoState must be used within <DemoStateProvider>");
  return ctx;
}
